import type { SortState } from "../hooks/useSortableRows";

interface SortableThProps<K extends string> {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (key: K) => void;
  /** Right-aligns the header + its click target — for numeric/currency columns. */
  align?: "left" | "right";
  /**
   * Pins this column to the left edge of a horizontally-scrolling table
   * (V2M-DEV-610 mobile pass) — every table in the panel scrolls sideways on
   * narrow screens rather than hiding columns, and the leading/identifying
   * column stays visible via `position: sticky` so an operator never loses
   * track of which row is which mid-scroll. Set on exactly the first column.
   */
  sticky?: boolean;
}

const BASE_TH = "px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider select-none";
// Matches the `bg-slate-50` every table's <thead> already uses — a sticky
// cell needs an opaque background or the body's scrolled-under content shows
// through it.
const STICKY_TH = "sticky left-0 z-10 bg-slate-50";

/**
 * A sortable `<th>` — every admin-panel table uses this for consistency
 * (PK 2026-07-25: sorting must be uniform across the whole panel, not
 * bolted onto one table at a time). Click toggles asc/desc on the active
 * column; clicking a different column switches to it descending (see
 * `useSortableRows.toggleSort` — counts/costs/dates dominate this panel,
 * so "biggest/newest first" is the useful first click).
 */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  sticky = false,
}: SortableThProps<K>) {
  const isActive = sort.key === sortKey;
  const ariaSort = !isActive ? "none" : sort.direction === "asc" ? "ascending" : "descending";
  const arrow = !isActive ? null : sort.direction === "asc" ? "▲" : "▼";

  return (
    <th
      className={`${BASE_TH} ${align === "right" ? "text-right" : "text-left"} ${sticky ? STICKY_TH : ""}`}
      aria-sort={ariaSort}
      scope="col"
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-700 transition-colors ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        <span>{label}</span>
        <span className="text-[10px] text-slate-400 w-2.5 inline-block">{arrow}</span>
      </button>
    </th>
  );
}
